import { Home, Dice5, Target, Settings, HelpCircle } from 'lucide-react';
import { useEffect } from 'react';
import { Outlet, NavLink } from 'react-router-dom';
import { toast } from 'sonner';
import { useRegisterSW } from 'virtual:pwa-register/react';
import { SideNav } from './SideNav';
import { Toaster } from '@/components/ui/sonner';
import { useIsLandscape } from '@/hooks/useIsLandscape';
import { useSwipeNavigation } from '@/hooks/useSwipeNavigation';
import { cn } from '@/lib/utils';
import { connectionManager } from '@/services/connectionManager';
import { sessionToEndpoints, getEndpointIdsForSession } from '@/services/sessionAdapter';
import { useActiveConnectionsStore } from '@/stores/activeConnectionsStore';
import { useSessionsStore } from '@/stores/sessionsStore';

/**
 * Main layout with adaptive navigation.
 * Shows bottom nav in portrait, side rail in landscape.
 */
export function Layout() {
  const isLandscape = useIsLandscape();
  const swipeHandlers = useSwipeNavigation();
  const { autoConnectSessionIds, sessions } = useSessionsStore();
  const getStatus = useActiveConnectionsStore((s) => s.getStatus);

  /** Register the service worker and show a toast when an update is available. */
  const { updateServiceWorker } = useRegisterSW({
    onNeedRefresh() {
      toast('Update available', {
        description: `v${__APP_VERSION__} (${__GIT_HASH__}) is ready`,
        action: { label: 'Update', onClick: () => updateServiceWorker(true) },
        duration: Infinity,
      });
    },
  });

  /**
   * Auto-reconnect sessions the user had connected on app mount / page refresh.
   *
   * Iterates persisted autoConnectSessionIds and connects each session that
   * still exists and isn't already active. Runs once on mount.
   * Fails silently so it never blocks the UI.
   */
  useEffect(() => {
    if (autoConnectSessionIds.length === 0) return;

    void (async () => {
      for (const sessionId of autoConnectSessionIds) {
        const session = sessions.find((s) => s.id === sessionId);
        if (!session) continue;

        // Skip if already connected or connecting (e.g. React hot-reload)
        const endpointIds = getEndpointIdsForSession(sessionId);
        const alreadyActive = endpointIds.some((id) => {
          const status = getStatus(id);
          return status === 'connected' || status === 'connecting';
        });
        if (alreadyActive) continue;

        const endpoints = sessionToEndpoints(session);
        try {
          for (const endpoint of endpoints) {
            await connectionManager.connect(endpoint);
          }
        } catch {
          // Auto-reconnect failures are silent — user can connect manually
        }
      }
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className={cn(
      'flex h-svh bg-background overflow-hidden',
      isLandscape ? 'flex-row' : 'flex-col'
    )}>
      {isLandscape && <SideNav />}
      <main className="flex-1 min-h-0 min-w-0 overflow-hidden" {...swipeHandlers}>
        <Outlet />
      </main>
      {!isLandscape && <BottomNav />}
      <Toaster />
    </div>
  );
}

/**
 * Bottom navigation bar for portrait mode.
 */
function BottomNav() {
  return (
    <nav className="relative flex justify-around border-t border-primary/30 bg-gradient-to-t from-secondary to-background py-2 pb-[max(0.5rem,env(safe-area-inset-bottom))]">
      {/* Decorative top glow line */}
      <div className="absolute top-0 left-4 right-4 h-px bg-gradient-to-r from-transparent via-primary/50 to-transparent" />
      <NavItem to="/" icon={Home} label="Home" />
      <NavItem to="/roll" icon={Dice5} label="Roll" />
      <NavItem to="/practice" icon={Target} label="Practice" />
      <NavItem to="/settings" icon={Settings} label="Settings" />
      <NavItem to="/about" icon={HelpCircle} label="Help" />
    </nav>
  );
}

interface NavItemProps {
  to: string;
  icon: React.ElementType;
  label: string;
}

/**
 * Navigation item with glow effect on active state.
 */
function NavItem({ to, icon: Icon, label }: NavItemProps) {
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        cn(
          'flex flex-col items-center gap-1 rounded-lg px-3 py-2 text-xs transition-all duration-200',
          isActive
            ? 'text-primary drop-shadow-[0_0_8px_hsl(var(--glow)/0.5)]'
            : 'text-muted-foreground hover:text-foreground hover:drop-shadow-[0_0_4px_hsl(var(--glow)/0.25)]'
        )
      }
    >
      <Icon className="h-5 w-5" />
      <span>{label}</span>
    </NavLink>
  );
}
